import { MouseEvent, useCallback, useContext, useState } from 'react';
import CreateReportModal from '@/modules/common/components/modals/CreateReportModal.tsx';
import {
  useGetReportsTemplatesQuery,
  useUpdateReportTemplateMutation,
} from '@/modules/admin-dashboard/queries/reports-templates.ts';
import { ChartEmptyState, InfoCard, ModalProviderContext } from 'ui';
import { ArrowLeftGayIcon, ArrowLeftIcon, FileIcon, PinFilledBlueIcon, PinIcon } from 'ui/icons';

const ReportTemplates = () => {
  const { openModal } = useContext(ModalProviderContext);
  const [page, setPage] = useState<number>(1);

  const { data, isFetching, hasNextPage } = useGetReportsTemplatesQuery({ page, page_size: 5 });

  const { mutateAsync: updateTemplate, isLoading: isTemplateUpdating } =
    useUpdateReportTemplateMutation();

  const templates = (data?.pages || []).flatMap(({ data }) => data);

  const totalItems = data?.pages?.[0]?.total || 0;

  const firstItemShownNumber = (page - 1) * 5 + 1;
  const lastItemShownNumber = Math.min((page - 1) * 5 + 5, totalItems);

  const isPossibleGoBack = !isFetching && page > 1;
  const isPossibleGoForward = !isFetching && hasNextPage;

  const goToPreviousPage = useCallback(() => {
    setPage(page => page - 1);
  }, []);

  const goToNextPage = useCallback(() => {
    setPage(page => page + 1);
  }, []);

  const handleOpenCreateReportWithTemplateModal = useCallback(
    (templateId: number) => {
      openModal(CreateReportModal, { templateId });
    },
    [openModal],
  );

  const handleToggleFavoriteTemplate = useCallback(
    (id: number, is_favorite: boolean, e: MouseEvent) => {
      e.stopPropagation();
      updateTemplate({ id, is_favorite });
    },
    [updateTemplate],
  );

  const bottomContent = !!templates.length && (
    <div className="w-full flex flex-row justify-between">
      <div className="text-sm text-neutral-500">
        {firstItemShownNumber} - {lastItemShownNumber} of {totalItems}
      </div>

      <div className="flex flex-row gap-6">
        <button onClick={goToPreviousPage} disabled={!isPossibleGoBack}>
          <img alt="back" src={isPossibleGoBack ? ArrowLeftIcon : ArrowLeftGayIcon} />
        </button>
        <button onClick={goToNextPage} disabled={!isPossibleGoForward}>
          <img
            alt="forward"
            src={isPossibleGoForward ? ArrowLeftIcon : ArrowLeftGayIcon}
            className="rotate-180"
          />
        </button>
      </div>
    </div>
  );

  return (
    <InfoCard title="Quick Reports" bottomAction={bottomContent}>
      <div className="w-full h-[264px] min-h-[250px]">
        {isFetching && (
          <div className="flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isLoading={isFetching} />
          </div>
        )}
        {!isFetching && !templates.length && (
          <div className="w-full h-full flex items-center justify-center">
            <p className="font-normal text-center">No report templates yet</p>
          </div>
        )}
        {!isFetching && !!templates.length && (
          <div className="flex flex-col gap-4">
            {templates.map(template => (
              <div key={template.id} className="flex items-center justify-between">
                <div
                  className="group flex gap-3 items-center cursor-pointer"
                  onClick={handleOpenCreateReportWithTemplateModal.bind(null, template.id)}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200">
                    <img alt="template" src={FileIcon} />
                  </div>
                  <div className="text-sm group-hover:underline">{template.name}</div>
                </div>

                <button
                  onClick={handleToggleFavoriteTemplate.bind(
                    null,
                    template.id,
                    !template.is_favorite,
                  )}
                  disabled={isTemplateUpdating}
                >
                  <img alt="favorite" src={template.is_favorite ? PinFilledBlueIcon : PinIcon} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </InfoCard>
  );
};

export default ReportTemplates;
